/**
 * IELTS listening / writing / speaking report models for the student
 * IELTS result reports. Builds on ieltsReading.mjs, which handles the shared
 * question mapping of the /progress-report rows.
 */

import { buildIeltsReadingReport } from "./ieltsReading.mjs";

/**
 * assessment_definition.category of an IELTS skill mapped onto the report
 * skill name the IELTS report components take as their `skill` prop. Single
 * source of truth for the mapping — the aggregated invitation report reads it
 * to work out which paper each `definitions-detail` row belongs to. Mirrors
 * IELTS_CATEGORY_SKILLS in sensay-assessment-admin src/utils/ieltsSkills.js.
 */
export const IELTS_CATEGORY_SKILLS = {
  IELTS_READ: "READING",
  IELTS_LISTEN: "LISTENING",
  IELTS_WRITE: "WRITING",
  IELTS_SPEAK: "SPEAKING",
};

/**
 * Official IELTS Listening raw-score boundaries for a 40-question paper:
 * raw correct answers at or above minRaw map to the band.
 */
export const IELTS_LISTENING_BAND_TABLE = [
  { minRaw: 39, band: 9 },
  { minRaw: 37, band: 8.5 },
  { minRaw: 35, band: 8 },
  { minRaw: 32, band: 7.5 },
  { minRaw: 30, band: 7 },
  { minRaw: 26, band: 6.5 },
  { minRaw: 23, band: 6 },
  { minRaw: 18, band: 5.5 },
  { minRaw: 16, band: 5 },
  { minRaw: 13, band: 4.5 },
  { minRaw: 10, band: 4 },
  { minRaw: 8, band: 3.5 },
  { minRaw: 6, band: 3 },
  { minRaw: 4, band: 2.5 },
  { minRaw: 2, band: 2 },
  { minRaw: 1, band: 1 },
];

/**
 * Converts a raw correct-answer count into an IELTS listening band. Question
 * counts other than 40 are scaled onto the 40-question table proportionally,
 * same policy as the reading band.
 */
export function calculateIeltsListeningBand(correctCount, totalQuestions) {
  if (
    !Number.isFinite(correctCount) ||
    !Number.isFinite(totalQuestions) ||
    totalQuestions <= 0
  ) {
    return null;
  }
  const clamped = Math.min(Math.max(correctCount, 0), totalQuestions);
  const scaled =
    totalQuestions === 40
      ? clamped
      : Math.round((clamped / totalQuestions) * 40);
  const row = IELTS_LISTENING_BAND_TABLE.find((r) => scaled >= r.minRaw);
  return row ? row.band : 0;
}

/** Nearest half band, clamped to the 0-9 scale. Null-safe. */
export function roundToHalfBand(value) {
  if (!Number.isFinite(value)) return null;
  return Math.min(9, Math.max(0, Math.round(value * 2) / 2));
}

/** Mean of the finite bands in the list, rounded to a half band. */
export function meanBand(bands) {
  const usable = (bands || []).filter((b) => Number.isFinite(b));
  if (!usable.length) return null;
  return roundToHalfBand(usable.reduce((a, b) => a + b, 0) / usable.length);
}

/**
 * Maps a 0-1 score fraction onto the 0-9 band scale (nearest half band).
 * Used for the heuristic speaking scores, which have no official band table.
 */
export function fractionToBand(fraction) {
  if (!Number.isFinite(fraction)) return null;
  return roundToHalfBand(Math.min(Math.max(fraction, 0), 1) * 9);
}

function parseJsonSafe(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

/**
 * Listening report: the same parts/questions model as the reading report
 * (each part additionally carries its `audio` file when the exercise has
 * one), but the band comes from the listening conversion table.
 */
export function buildIeltsListeningReport(progressList) {
  const report = buildIeltsReadingReport(progressList);
  return {
    ...report,
    band: calculateIeltsListeningBand(
      report.totalCorrect,
      report.totalQuestions,
    ),
  };
}

const WRITING_CRITERIA = [
  { key: "TASK_RESPONSE", band: "taskResponse", feedback: "taskResponseFeedback" },
  { key: "COHERENCE_COHESION", band: "coherenceCohesion", feedback: "coherenceCohesionFeedback" },
  { key: "LEXICAL_RESOURCE", band: "lexicalResource", feedback: "lexicalResourceFeedback" },
  { key: "GRAMMATICAL_RANGE_ACCURACY", band: "grammaticalRangeAccuracy", feedback: "grammaticalRangeAccuracyFeedback" },
];

/**
 * Writing report from the WRITING progress rows. Task bands, criterion
 * feedback and the improved version come from the WRITING_LLM_IELTS
 * scorecard stored in reviewData; every field degrades to null when a
 * submission was scored by an older or different strategy.
 *
 * Returns { tasks, band, totalWords }.
 */
export function buildIeltsWritingReport(progressList) {
  const tasks = [];

  (progressList || []).forEach((item) => {
    if (!item || item.exerciseType !== "WRITING") return;
    const data = parseJsonSafe(item.data);
    const answerData = parseJsonSafe(item.answerData);
    const scorecard = parseJsonSafe(item.reviewData);

    const band = Number.isFinite(scorecard?.overallBand)
      ? scorecard.overallBand
      : null;
    const criteria = WRITING_CRITERIA.map((c) => ({
      key: c.key,
      band: Number.isFinite(scorecard?.[c.band]) ? scorecard[c.band] : null,
      feedback: scorecard?.[c.feedback] || null,
    })).filter((c) => c.band !== null || c.feedback);

    const improvedVersion = scorecard?.improvedVersion || null;

    tasks.push({
      number: tasks.length + 1,
      title: data?.title || item.name || "Task",
      prompt: data?.question || null,
      answerText: answerData?.writingAnswer || "",
      answered: String(answerData?.writingAnswer || "").trim().length > 0,
      wordCount: Number.isFinite(scorecard?.wordCount)
        ? scorecard.wordCount
        : answerData?.wordCount ?? null,
      minWords: Number.isFinite(scorecard?.minWords) ? scorecard.minWords : null,
      underMinWords: scorecard?.underMinWords === true,
      band,
      criteria,
      improvedVersion,
      improvedVersionBand: improvedVersion
        ? (Number.isFinite(scorecard?.improvedVersionBand)
            ? scorecard.improvedVersionBand
            : band !== null
              ? Math.min(9, band + 1)
              : null)
        : null,
    });
  });

  return {
    tasks,
    band: meanBand(tasks.map((t) => t.band)),
    totalWords: tasks.reduce((sum, t) => sum + (t.wordCount || 0), 0),
  };
}

/** Exercise types answered by speaking into the microphone. */
const SPEAKING_EXERCISE_TYPES = ["READ", "LISTEN_REPEAT", "QUESTION", "LISTEN", "VIDEO", "IMAGE"];

/**
 * Per-criterion sub-scores a speaking scorecard may carry, listed in the order
 * of the official IELTS speaking criteria (relevance is extra — it has no
 * official equivalent but is what the scorer reports for task response). Field
 * names follow the ones the TOEIC speaking report already reads. Not every
 * scoring strategy produces them, so missing fields are simply left out.
 */
const SPEAKING_SCORE_FIELDS = [
  { key: "FLUENCY", field: "normalizedFluencyScore" },
  { key: "LEXICAL_RESOURCE", field: "normalizedVocabScore" },
  { key: "GRAMMATICAL_RANGE_ACCURACY", field: "normalizedGrammarScore" },
  { key: "PRONUNCIATION", field: "normalizedPronScore" },
  { key: "RELEVANCE", field: "normalizedRelevanceScore" },
];

/**
 * Sub-scores are stored either as a 0-1 fraction or as a 0-100 percentage
 * depending on the scoring strategy, so anything above 1 is read as a
 * percentage. Returns null for values that cannot be read as either.
 */
export function scoreToFraction(value) {
  if (!Number.isFinite(value) || value < 0) return null;
  const fraction = value > 1 ? value / 100 : value;
  return fraction > 1 ? null : fraction;
}

const RECORDING_BASE_URL =
  "https://storage.googleapis.com/sensay-assessment/recordings/";

/**
 * Speaking report from the speech-recorded progress rows (SKIP-scored
 * warm-up items are left out). One score per answer: the scorecard's
 * scoreTotal/scoreTotalMax fraction (fallback: normalizedScore/scoreWeight)
 * mapped onto the 0-9 band scale. Transcripts are not in the progress rows —
 * the report component loads them per answer via exerciseResultId.
 *
 * Each answer also carries the raw score and whichever per-criterion
 * sub-scores the scorecard happens to hold, for the detail scoring panel.
 *
 * Returns { answers, band, answeredCount }.
 */
export function buildIeltsSpeakingReport(progressList) {
  const answers = [];

  (progressList || []).forEach((item) => {
    if (!item || !SPEAKING_EXERCISE_TYPES.includes(item.exerciseType)) return;
    if (item.scoringStrategy === "SKIP") return;
    const data = parseJsonSafe(item.data);
    const scorecard = parseJsonSafe(item.reviewData);

    let fraction = null;
    if (
      Number.isFinite(scorecard?.scoreTotal) &&
      Number.isFinite(scorecard?.scoreTotalMax) &&
      scorecard.scoreTotalMax > 0
    ) {
      fraction = scorecard.scoreTotal / scorecard.scoreTotalMax;
    } else if (Number.isFinite(item.normalizedScore)) {
      const weight = Number.isFinite(item.scoreWeight) ? item.scoreWeight : 1;
      fraction = weight > 0 ? item.normalizedScore / weight : null;
    }

    const scoreDetail = SPEAKING_SCORE_FIELDS.map((criterion) => ({
      key: criterion.key,
      fraction: scoreToFraction(scorecard?.[criterion.field]),
    }))
      .filter((criterion) => criterion.fraction !== null)
      .map((criterion) => ({
        ...criterion,
        band: fractionToBand(criterion.fraction),
      }));

    answers.push({
      number: answers.length + 1,
      title: data?.title || item.name || "Question",
      prompt: data?.question || data?.text || null,
      exerciseResultId: item.exerciseResultId || null,
      audioUrl: item.recordingFileUrl
        ? item.recordingFileUrl
        : item.recordingFileName
          ? RECORDING_BASE_URL + item.recordingFileName
          : null,
      answered: !!item.exerciseResultId,
      fraction,
      band: fractionToBand(fraction),
      scoreTotal: Number.isFinite(scorecard?.scoreTotal)
        ? scorecard.scoreTotal
        : null,
      scoreTotalMax: Number.isFinite(scorecard?.scoreTotalMax)
        ? scorecard.scoreTotalMax
        : null,
      scoreDetail,
    });
  });

  return {
    answers,
    band: meanBand(answers.map((a) => a.band)),
    answeredCount: answers.filter((a) => a.answered).length,
  };
}
